
import api from "@/api";
import { SystemLogService } from "@/api/dtos/SystemLogService.schema.d";
import ContainerFrame from "@/components/ContainerFrame.vue";
import { Component, Vue, Watch } from "vue-property-decorator";
import { getAxiosResponse } from "@/utils/errorUtils";
import { showToast } from "@/utils/toastService";

@Component({
  components: {
    ContainerFrame,
  },
})
export default class SystemLogsView extends Vue {
  services: SystemLogService[] = [];
  selectedService = "bishamon";
  sinceLocal = "";
  untilLocal = "";
  logTextValue = "";
  loadingServices = false;
  loadingLogs = false;
  downloading = false;
  private logRequestId = 0;

  async mounted(): Promise<void> {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

    this.sinceLocal = this.toDatetimeLocalValue(oneHourAgo);
    this.untilLocal = this.toDatetimeLocalValue(now);

    await this.fetchServices();
    await this.fetchLogs();
  }

  get serviceOptions(): { id: string; label: string }[] {
    return this.services.map((service) => ({
      id: service.id,
      label: service.available ? service.label : `${service.label} (keine Datei)`,
    }));
  }

  get logText(): string {
    if (this.loadingLogs) {
      return "Logs werden geladen...";
    }

    if (!this.logTextValue) {
      return "Keine Logeintraege im ausgewaehlten Zeitraum.";
    }

    return this.logTextValue;
  }

  get canRequestLogs(): boolean {
    return Boolean(this.selectedService && this.sinceLocal && this.untilLocal);
  }

  @Watch("selectedService")
  async onSelectedServiceChanged(): Promise<void> {
    this.logTextValue = "";
    await this.fetchLogs();
  }

  async fetchServices(): Promise<void> {
    this.loadingServices = true;
    try {
      this.services = await api.SystemLogsApi.getServices();

      if (!this.services.some((service) => service.id === this.selectedService)) {
        this.selectedService = this.services[0]?.id ?? "";
      }
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: "System-Log-Services konnten nicht geladen werden.",
        type: "error",
      });
    } finally {
      this.loadingServices = false;
    }
  }

  async fetchLogs(): Promise<void> {
    if (!this.canRequestLogs) {
      return;
    }

    const requestId = ++this.logRequestId;
    const service = this.selectedService;
    this.loadingLogs = true;
    try {
      const logText = await api.SystemLogsApi.getLogs(
        service,
        this.parseLocalDateTime(this.sinceLocal),
        this.parseLocalDateTime(this.untilLocal)
      );

      if (requestId === this.logRequestId && service === this.selectedService) {
        this.logTextValue = logText;
      }
    } catch (e) {
      if (requestId === this.logRequestId && service === this.selectedService) {
        this.logTextValue = "";
        showToast({
          response: getAxiosResponse(e),
          message: "System-Logs konnten nicht geladen werden.",
          type: "error",
        });
      }
    } finally {
      if (requestId === this.logRequestId && service === this.selectedService) {
        this.loadingLogs = false;
      }
    }
  }

  async downloadLogs(): Promise<void> {
    if (!this.canRequestLogs) {
      return;
    }

    this.downloading = true;
    try {
      await api.SystemLogsApi.downloadLogs(
        this.selectedService,
        this.parseLocalDateTime(this.sinceLocal),
        this.parseLocalDateTime(this.untilLocal)
      );
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: "System-Logs konnten nicht heruntergeladen werden.",
        type: "error",
      });
    } finally {
      this.downloading = false;
    }
  }

  private parseLocalDateTime(value: string): Date {
    return new Date(value);
  }

  private toDatetimeLocalValue(date: Date): string {
    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().slice(0, 16);
  }
}
